import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Sparkles, SendHorizonal, Trash2, Wind, Play } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { streamPost } from "@/lib/stream";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";

const STARTERS = [
  "I feel wound up — what should I try?",
  "How do I do box breathing properly?",
  "What do you notice in my practice this week?",
  "Why does a longer exhale help?",
];

const renderText = (text) =>
  text.split(/(\[[^\]]+\]\(\/tutorials\/[^)]+\)|\*\*[^*]+\*\*)/g).filter(Boolean).map((p, i) => {
    const link = p.match(/^\[([^\]]+)\]\((\/tutorials\/[^)]+)\)$/);
    if (link) return <Link key={i} to={link[2]} className="inline-flex items-center gap-1 font-semibold text-indigo-700 underline decoration-indigo-300 underline-offset-2 hover:decoration-indigo-600" data-testid="companion-tutorial-link"><Wind className="w-3.5 h-3.5" />{link[1]}</Link>;
    if (p.startsWith("**")) return <strong key={i} className="font-semibold">{p.slice(2, -2)}</strong>;
    return <Fragment key={i}>{p}</Fragment>;
  });

const linkedTutorialIds = (text) => [...new Set([...text.matchAll(/\]\(\/tutorials\/([^)\s]+)\)/g)].map((m) => m[1]))];

const StartButtons = ({ content, tutorials }) => {
  const ids = linkedTutorialIds(content).filter((id) => tutorials[id]);
  if (ids.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2" data-testid="companion-start-buttons">
      {ids.map((id) => {
        const t = tutorials[id];
        const to = t.breathing_step_index != null ? `/tutorials/${id}?step=${t.breathing_step_index}&focus=breathing` : `/tutorials/${id}`;
        return (
          <Link key={id} to={to} className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 text-white text-xs sm:text-sm font-semibold px-4 py-2 hover:bg-indigo-700 transition-colors shadow-sm" data-testid={`companion-start-${id}`}>
            <Play className="w-3.5 h-3.5" /> {t.breathing_step_index != null ? "Start" : "Open"} {t.title}
          </Link>
        );
      })}
    </div>
  );
};

const Bubble = ({ m, tutorials, isLast, onSuggest, busy }) => {
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`companion-message-${m.role}`}>
      <div className="max-w-[85%] sm:max-w-[70%]">
        <div className={`rounded-3xl px-4 py-3 text-sm sm:text-base leading-relaxed whitespace-pre-wrap ${mine ? "bg-indigo-600 text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-800 rounded-bl-md"}`}>
          {m.content ? renderText(m.content) : <span className="inline-flex gap-1 py-1"><span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" /><span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:150ms]" /><span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:300ms]" /></span>}
        </div>
        {!mine && m.content && <StartButtons content={m.content} tutorials={tutorials} />}
        {!mine && isLast && !busy && m.suggestions?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2" data-testid="companion-quick-replies">
            {m.suggestions.map((s) => (
              <button key={s} type="button" onClick={() => onSuggest(s)} className="px-3 py-1.5 rounded-full border border-indigo-200 bg-indigo-50/60 text-xs sm:text-sm text-indigo-800 hover:bg-indigo-100 hover:border-indigo-300 transition-colors" data-testid="companion-quick-reply">{s}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default function Companion() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [messages, setMessages] = useState(null);
  const [tutorials, setTutorials] = useState({});
  const [input, setInput] = useState(() => searchParams.get("prefill") || "");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/companion/messages").then((r) => setMessages(r.data)).catch((e) => { toast.error(errorMessage(e)); setMessages([]); });
    api.get("/tutorials").then((r) => setTutorials(Object.fromEntries(r.data.map((t) => [t.id, t])))).catch(() => {});
    if (searchParams.get("prefill")) { setSearchParams({}, { replace: true }); setTimeout(() => inputRef.current?.focus(), 50); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setInput("");
    setBusy(true);
    const tempId = `tmp-${Date.now()}`;
    setMessages((m) => [...m, { id: `${tempId}-u`, role: "user", content }, { id: tempId, role: "assistant", content: "" }]);
    const patch = (fn) => setMessages((m) => m.map((x) => (x.id === tempId ? fn(x) : x)));
    try {
      const r = await streamPost("/companion/chat", { message: content }, { onDelta: (d) => patch((x) => ({ ...x, content: x.content + d })) });
      patch((x) => ({ ...x, id: r.id || x.id, suggestions: r.suggestions || [] }));
    } catch (e) {
      toast.error(e.message);
      setMessages((m) => m.filter((x) => x.id !== tempId));
    } finally { setBusy(false); }
  };

  const clear = async () => {
    try { await api.delete("/companion/messages"); setMessages([]); toast.success("Conversation cleared"); } catch (e) { toast.error(errorMessage(e)); }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12 flex flex-col min-h-[calc(100vh-4rem)]" data-testid="companion-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="eyebrow">Calm Companion</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 mt-2">Hi {user?.name?.split(" ")[0] || "there"}</h1>
          <p className="text-base md:text-lg text-slate-600 mt-2">Ask about any exercise, say how you feel, or reflect on your practice. Supportive guidance — not therapy; your therapist never sees this chat.</p>
        </div>
        {messages?.length > 0 && <Button variant="ghost" size="sm" onClick={clear} className="rounded-full text-slate-500" data-testid="companion-clear-button"><Trash2 className="w-4 h-4" /> Clear</Button>}
      </div>

      <div className="flex-1 rounded-3xl bg-slate-50 border border-slate-200 p-4 sm:p-6 space-y-4 overflow-y-auto min-h-[40vh] max-h-[58vh]" data-testid="companion-thread">
        {!messages ? <Skeleton className="h-40 rounded-2xl" /> : messages.length === 0 ? (
          <div className="text-center py-10" data-testid="companion-empty">
            <span className="w-12 h-12 rounded-2xl bg-indigo-100 text-indigo-600 grid place-items-center mx-auto mb-4"><Sparkles className="w-6 h-6" /></span>
            <p className="text-slate-600 mb-6">A few things you could ask:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {STARTERS.map((s) => <button key={s} type="button" onClick={() => send(s)} className="px-4 py-2 rounded-full bg-white border border-slate-200 text-sm text-slate-700 hover:border-indigo-300 hover:text-indigo-700 transition-colors" data-testid="companion-starter">{s}</button>)}
            </div>
          </div>
        ) : messages.map((m, i) => <Bubble key={m.id} m={m} tutorials={tutorials} isLast={i === messages.length - 1} onSuggest={send} busy={busy} />)}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4 flex items-end gap-2" data-testid="companion-form">
        <Textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey} rows={1} maxLength={2000} placeholder="Say how you feel or ask about an exercise…" className="rounded-2xl resize-none min-h-[48px] bg-white" data-testid="companion-input" />
        <Button type="submit" disabled={busy || !input.trim()} className="rounded-2xl h-12 w-12 p-0 bg-indigo-600 hover:bg-indigo-700" aria-label="Send" data-testid="companion-send-button"><SendHorizonal className="w-5 h-5" /></Button>
      </form>
      <p className="text-xs text-slate-400 mt-2">If you are in crisis, contact your therapist or local emergency services (US: call or text 988).</p>
    </div>
  );
}
